import { useMemo, createContext } from "react";

import { DndContext } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { Flex, Typography, Button, Card, Divider, Modal, Tag } from "antd";

import PlusIcon from "@/icons/PlusIcon";

import GroupCard from "./GroupCard";

import { deleteGroup, updateGroup } from "@/api/requests";
import { useGroupSync } from "../hooks/useGroupSync";

const getFieldIdsFromTaskTitleTemplate = (template = "") => {
  const fieldIds = new Set();
  const regex = /\{f:([^{}]+)\}/g;

  let match;

  while ((match = regex.exec(template)) !== null) {
    fieldIds.add(match[1]);
  }

  return fieldIds;
};

const CONTACT_GROUP = {
  id: "contact_group_static",
  name: "Контактные данные",
  isStatic: true,
  fields: [
    { uuid: "contact_name", name: "Ваши ФИО", type: "text", is_required: true },
    { uuid: "contact_email", name: "Email", type: "text", is_required: true },
    {
      uuid: "contact_phone",
      name: "Телефон",
      type: "text",
      is_required: false,
    },
  ],
};

const GroupsCard = ({
  data = {},
  groups,
  id,
  isDeleted,
  isDragDisabled,
  fetchData,
  addGroup,
  toggleEdit,
  pending,
  setPending,
  setGroups,
}) => {
  const taskTitleTemplate = data?.task_name_template || "";

  const templateFieldIds = useMemo(
    () => getFieldIdsFromTaskTitleTemplate(taskTitleTemplate),
    [taskTitleTemplate],
  );

  const sortedGroups = useMemo(() => {
    return [...groups].sort((a, b) => {
      // новые группы (без idx) всегда в конце
      if (!a.idx) return 1;
      if (!b.idx) return -1;

      return a.idx - b.idx;
    });
  }, [groups]);

  const isAnyGroupEditing = useMemo(
    () => groups.some((g) => g.isEditing),
    [groups],
  );

  const { saveGroup } = useGroupSync({
    id,
    fetchData,
    setPending,
  });

  const SortableGroupContext = createContext({});

  const SortableGroupItem = ({ itemKey, children, disabled }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: itemKey,
      disabled: !!disabled,
    });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { zIndex: 12 } : {}),
    };

    const ctx = useMemo(
      () => ({ setActivatorNodeRef, listeners, attributes }),
      [setActivatorNodeRef, listeners, attributes],
    );

    return (
      <SortableGroupContext.Provider value={ctx}>
        <div ref={setNodeRef} style={style}>
          {children}
        </div>
      </SortableGroupContext.Provider>
    );
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = sortedGroups.findIndex((g) => g.id === active.id);
    const newIndex = sortedGroups.findIndex((g) => g.id === over.id);

    const movedGroup = sortedGroups[oldIndex];

    const newGroups = [...sortedGroups];
    newGroups.splice(oldIndex, 1);
    newGroups.splice(newIndex, 0, movedGroup);

    setGroups(newGroups);

    // если группа ещё не сохранена — ничего не делаем
    if (movedGroup.isNew) return;

    try {
      setPending(true);

      await updateGroup({
        uuid: movedGroup.uuid,
        idx: newIndex + 1,
      });

      await fetchData();
    } catch (e) {
      console.error("Ошибка при изменении порядка", e);
    } finally {
      setPending(false);
    }
  };

  const getGroupFieldsUsedInTitleTemplate = (group) =>
    (group.fields || []).filter((field) => templateFieldIds.has(field.uuid));

  const handleRemoveGroup = async (group) => {
    const usedFields = getGroupFieldsUsedInTitleTemplate(group);

    if (usedFields.length) {
      Modal.warning({
        title: "Невозможно удалить группу",
        content: (
          <Flex vertical gap={8}>
            <Typography.Text>
              Данные поля группы используются в шаблоне названия задачи:
            </Typography.Text>

            <Flex wrap gap={6}>
              {usedFields.map((field) => (
                <Tag
                  key={field.uuid}
                  color="purple"
                  style={{
                    maxWidth: 180,
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={field.name}
                  >
                    {field.name}
                  </span>
                </Tag>
              ))}
            </Flex>
          </Flex>
        ),
        okText: "Закрыть",
      });

      return;
    }

    Modal.confirm({
      title: `Вы уверены, что хотите удалить группу полей${
        group?.name ? ` ${group?.name}` : ""
      }?`,
      onOk() {
        handleConfirmDelete(group);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  const handleConfirmDelete = async (group) => {
    if (group.isNew) {
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      return;
    }

    await deleteGroup({ uuid: group.id });
    setGroups((prev) => prev.filter((g) => g.id !== group.id));
    fetchData();
  };
  return (
    <Card
      title={
        <Typography.Title level={4} className="no-margin">
          Группы полей
        </Typography.Title>
      }
    >
      <Flex vertical>
        <GroupCard // Контактные данные
          group={CONTACT_GROUP}
          index={0}
          isDeleted={false}
          isAnyGroupEditing={false}
          pending={false}
          isStatic
        />

        <Divider style={{ borderColor: "var(--color-gray)" }} />

        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={isDeleted || isDragDisabled ? undefined : handleDragEnd}
        >
          <SortableContext
            items={sortedGroups.map((g) => g.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedGroups.map((group, index) => (
              <SortableGroupItem
                key={group.id}
                itemKey={group.id}
                disabled={isDragDisabled || isDeleted}
              >
                <GroupCard
                  templateFieldIds={templateFieldIds}
                  pending={pending}
                  group={group}
                  index={index}
                  onSave={saveGroup}
                  onRemove={handleRemoveGroup}
                  toggleEdit={toggleEdit}
                  SortableGroupContext={SortableGroupContext}
                  isDeleted={isDeleted}
                  isAnyGroupEditing={isAnyGroupEditing}
                />
                <Divider style={{ borderColor: "var(--color-gray)" }} />
              </SortableGroupItem>
            ))}
          </SortableContext>
        </DndContext>

        {!isDeleted && (
          <Button
            disabled={isAnyGroupEditing}
            type="dashed"
            icon={<PlusIcon />}
            onClick={addGroup}
          >
            Новая группа полей
          </Button>
        )}
      </Flex>
    </Card>
  );
};

export default GroupsCard;
